/**
 * Why Now Slide
 * 50/50 layout: SVG architecture diagram (left) | animated demo panel (right)
 * Architecture highlights sync with the demo panel's reveal sequence.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import { colors } from '@config/colors';
import { typography } from '@config/typography';
import { spacing, borderRadius } from '@config/spacing';
import { media } from '@config/breakpoints';
import { SlideBase, SlideTitle, SlideSubtitle } from '@components/press/pitch-deck/slide-primitives';
import { ArchDiagramSvg } from '@components/press/pitch-deck/slides/product/arch-diagram-svg';

// ============================================
// Scenario Data
// ============================================

interface DemoScenario {
  id: string;
  title: string;
  icon: string;
  humanAsk: string;
  aiTasks: string[];
  aiResponse: string;
}

const SCENARIOS: DemoScenario[] = [
  {
    id: 'date-night',
    title: 'Push Button Date Night',
    icon: '\u{1F496}',
    humanAsk: 'Plan a date night for this Saturday',
    aiTasks: [
      'Check household calendar for conflicts',
      'Message preferred babysitters',
      'Book restaurant matching preferences',
      'Confirm babysitter availability',
      'Add to both partners\u2019 calendars',
      'Send partner a confirmation',
    ],
    aiResponse:
      'Your Date Night is successfully scheduled and confirmed. Please let me know how it goes so I can update your preferences for future date nights.',
  },
  {
    id: 'childcare',
    title: 'Image to Childcare',
    icon: '\u{1F4C5}',
    humanAsk: 'Here\u2019s a photo of the preschool calendar',
    aiTasks: [
      'Extract dates and events from image',
      'Cross-reference with household calendar',
      'Create recurring childcare reminders',
      'Sync to family shared calendar',
      'Set up 2-day advance alerts',
    ],
    aiResponse:
      'All 14 events synced to your family calendar with childcare reminders. I\u2019ll alert you 2 days before each one.',
  },
  {
    id: 'growth',
    title: 'How Tall Will My Kid Be?',
    icon: '\u{1F4CF}',
    humanAsk: 'How tall will Emma be? She\u2019s 42\u2033 at age 4',
    aiTasks: [
      'Pull Emma\u2019s health records from memory',
      'Run CDC/WHO growth percentile models',
      'Cross-reference with parental heights',
      'Generate sport aptitude analysis',
      'Prepare doctor visit questions',
    ],
    aiResponse:
      'Emma is tracking at the 75th percentile \u2014 projected 5\u20197\u2033-5\u201910\u2033. I\u2019ve prepared growth charts and questions for her next checkup.',
  },
];

// ============================================
// Highlight Mapping
// Maps (scenarioIndex, revealIndex) → architecture diagram highlights
// ============================================

interface HighlightState {
  nodes: string[];
  connections: string[];
}

// Per-scenario, per-task-index: which architecture nodes/connections light up
const TASK_HIGHLIGHT_MAP: HighlightState[][][] = [
  // Scenario 0: Date Night
  [
    [{ nodes: ['agents', 'tools'], connections: ['agents-tools'] }],
    [{ nodes: ['agents', 'tools', 'memory', 'context'], connections: ['agents-tools', 'context-memory'] }],
    [{ nodes: ['agents', 'tools', 'memory', 'context'], connections: ['agents-tools', 'context-memory'] }],
    [{ nodes: ['agents', 'tools'], connections: ['agents-tools'] }],
    [{ nodes: ['agents', 'tools'], connections: ['agents-tools'] }],
    [{ nodes: ['agents', 'tools'], connections: ['agents-tools'] }],
  ],
  // Scenario 1: Childcare
  [
    [{ nodes: ['agents', 'context'], connections: ['agents-context'] }],
    [{ nodes: ['agents', 'tools', 'context'], connections: ['agents-tools', 'agents-context'] }],
    [{ nodes: ['agents', 'tools'], connections: ['agents-tools'] }],
    [{ nodes: ['agents', 'tools'], connections: ['agents-tools'] }],
    [{ nodes: ['agents', 'tools'], connections: ['agents-tools'] }],
  ],
  // Scenario 2: Growth
  [
    [{ nodes: ['agents', 'memory', 'context'], connections: ['context-memory', 'agents-context'] }],
    [{ nodes: ['agents', 'research', 'context'], connections: ['context-research', 'agents-context'] }],
    [{ nodes: ['agents', 'memory', 'context'], connections: ['context-memory', 'agents-context'] }],
    [{ nodes: ['agents', 'research', 'context'], connections: ['context-research', 'agents-context'] }],
    [{ nodes: ['agents', 'instructions'], connections: ['agents-instructions'] }],
  ],
];

// Per-scenario, per-task-index: which specific pills inside highlighted cards
// get the secondary (amber) highlight to show exactly what's being used.
const PILL_HIGHLIGHT_MAP: string[][][] = [
  // Scenario 0: Date Night
  [
    ['Calendar'],                     // Check household calendar
    ['Contacts', 'SMS'],              // Message preferred babysitters
    ['Preferences', 'Web Search'],    // Book restaurant matching preferences
    ['SMS'],                          // Confirm babysitter availability
    ['Calendar'],                     // Add to both partners' calendars
    ['SMS'],                          // Send partner a confirmation
  ],
  // Scenario 1: Childcare
  [
    [],                               // Extract dates from image
    ['Calendar'],                     // Cross-reference with calendar
    ['Reminders'],                    // Create recurring reminders
    ['Calendar'],                     // Sync to family calendar
    ['Reminders'],                    // Set up alerts
  ],
  // Scenario 2: Growth
  [
    ['Facts', 'Reports'],             // Pull Emma's health records
    ['Health'],                       // Run growth percentile models
    ['Household Relationships'],      // Cross-reference parental heights
    ['Health'],                       // Generate sport aptitude analysis
    ['Interface Responses'],          // Prepare doctor visit questions
  ],
];

function getPillHighlights(scenarioIndex: number, revealIndex: number): Set<string> {
  if (revealIndex < 0 || revealIndex >= (PILL_HIGHLIGHT_MAP[scenarioIndex]?.length ?? 0)) {
    return new Set();
  }
  return new Set(PILL_HIGHLIGHT_MAP[scenarioIndex][revealIndex]);
}

function getHighlightsForStage(
  scenarioIndex: number,
  revealIndex: number,
): { nodes: Set<string>; connections: Set<string> } {
  // Human asking phase or AI response phase
  if (revealIndex <= -1 || revealIndex >= SCENARIOS[scenarioIndex].aiTasks.length) {
    return {
      nodes: new Set(['interface', 'agents']),
      connections: new Set(['interface-agents']),
    };
  }

  // Task processing phase
  const taskHighlights = TASK_HIGHLIGHT_MAP[scenarioIndex]?.[revealIndex]?.[0];
  if (taskHighlights) {
    return {
      nodes: new Set(taskHighlights.nodes),
      connections: new Set(taskHighlights.connections),
    };
  }

  return { nodes: new Set(['agents']), connections: new Set() };
}

// ============================================
// Slide Layout
// ============================================

const SlideLayout = styled(SlideBase)`
  align-items: stretch;
  justify-content: flex-start;
  padding: 0;

  &::before, &::after {
    display: none;
  }

  ${media.md} {
    padding: 0;
  }
`;

const Header = styled.div`
  text-align: center;
  flex-shrink: 0;
  padding: ${spacing.md} ${spacing.lg} 0;

  ${media.md} {
    padding: ${spacing.lg} ${spacing['2xl']} 0;
  }
`;


const ContentArea = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: stretch;
  padding: ${spacing.sm} ${spacing.md} ${spacing.md};
  gap: ${spacing.md};

  ${media.md} {
    padding: ${spacing.md} ${spacing.xl} ${spacing.lg};
    gap: ${spacing.lg};
  }
`;

const DiagramPanel = styled.div`
  flex: 0 0 50%;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.sm};
`;

const DemoPanel = styled.div`
  flex: 1;
  min-height: 0;
  max-height: 75%;
  align-self: flex-start;
  margin-top: ${spacing['2xl']};
  display: flex;
  flex-direction: column;
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.lg};
  overflow: hidden;
  background: #fff;
`;

// ============================================
// Demo Panel — Styled Components
// ============================================

const PanelContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ScenarioTabs = styled.div`
  display: flex;
  gap: ${spacing.xs};
  padding: ${spacing.xs} ${spacing.sm};
  flex-shrink: 0;
`;

const Tab = styled.button<{ $active: boolean }>`
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.xs}, 1.2vw, ${typography.fontSize.sm});
  padding: 3px 8px;
  border-radius: ${borderRadius.full};
  border: 1px solid ${(p) => (p.$active ? colors.brand.exxtraDark : colors.neutral.light)};
  background: ${(p) => (p.$active ? colors.brand.primary : 'transparent')};
  color: ${(p) => (p.$active ? colors.highlight.dark : colors.highlight.light)};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    border-color: ${colors.brand.primaryDark};
  }
`;

const SectionLabel = styled.div<{ $color?: string }>`
  font-family: ${typography.fontFamily.heading};
  font-size: clamp(${typography.fontSize.xs}, 1.2vw, ${typography.fontSize.sm});
  font-weight: ${typography.fontWeight.semibold};
  color: ${(p) => p.$color || colors.highlight.light};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 4px;
`;

const AnimatedBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  overflow-y: auto;
`;

const HumanBubble = styled.div`
  background: ${colors.accent.coral.exxtraLight}60;
  border: 1px solid ${colors.accent.coral.dark}30;
  border-radius: ${borderRadius.md};
  padding: ${spacing.sm} ${spacing.md};
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.sm}, 1.4vw, ${typography.fontSize.base});
  color: ${colors.highlight.dark};
  line-height: 1.5;
`;

const TaskList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const AiBubble = styled.div`
  background: ${colors.brand.primary}50;
  border: 1px solid ${colors.brand.primaryDark}40;
  border-radius: ${borderRadius.md};
  padding: ${spacing.sm} ${spacing.md};
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.sm}, 1.4vw, ${typography.fontSize.base});
  color: ${colors.highlight.dark};
  line-height: 1.5;
  font-style: italic;
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const TaskItemAnimated = styled(motion.div)<{ $done: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.sm}, 1.3vw, ${typography.fontSize.base});
  color: ${(p) => (p.$done ? colors.highlight.primary : colors.highlight.extraLight)};
  padding: 2px 0;

  ${(p) =>
    !p.$done &&
    css`
      animation: ${pulse} 1.2s ease-in-out infinite;
    `}
`;

const TaskIcon = styled.span<{ $done: boolean }>`
  font-size: clamp(${typography.fontSize.sm}, 1.3vw, ${typography.fontSize.base});
  color: ${(p) => (p.$done ? colors.accent.green.extraDark : colors.highlight.extraLight)};
`;

const WipBanner = styled.div`
  flex-shrink: 0;
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.accent.coral.exxtraLight};
  border-top: 1px solid ${colors.accent.coral.dark};
  font-family: ${typography.fontFamily.heading};
  font-size: clamp(${typography.fontSize.xs}, 1.2vw, ${typography.fontSize.sm});
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.accent.coral.exxtraDark};
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

// ============================================
// Demo Panel — Component
// ============================================

const STEP_INTERVAL = 1200; // ms per reveal step

interface DemoPanelProps {
  activeScenario: number;
  revealIndex: number;
  onSelectScenario: (index: number) => void;
}

const DemoPanelAnimated: React.FC<DemoPanelProps> = ({
  activeScenario,
  revealIndex,
  onSelectScenario,
}) => {
  const s = SCENARIOS[activeScenario];
  const showResponse = revealIndex >= s.aiTasks.length;

  return (
    <PanelContainer>
      <ScenarioTabs>
        {SCENARIOS.map((sc, i) => (
          <Tab
            key={sc.id}
            $active={i === activeScenario}
            onClick={() => onSelectScenario(i)}
          >
            {sc.title}
          </Tab>
        ))}
      </ScenarioTabs>

      <AnimatedBody>
        <AnimatePresence mode="wait">
          <motion.div
            key={activeScenario}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}
          >
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <SectionLabel $color={colors.accent.coral.exxtraDark}>Human</SectionLabel>
              <HumanBubble>&ldquo;{s.humanAsk}&rdquo;</HumanBubble>
            </motion.div>

            {revealIndex >= 0 && (
              <div>
                <SectionLabel $color={colors.brand.exxtraDark}>AI Does</SectionLabel>
                <TaskList>
                  {s.aiTasks.map((task, i) => {
                    const done = i < revealIndex;
                    const current = i === revealIndex;
                    if (i > revealIndex) return null;
                    return (
                      <TaskItemAnimated key={task} $done={done || current}>
                        <TaskIcon $done={done}>
                          {done ? '\u2713' : '\u25CB'}
                        </TaskIcon>
                        {task}
                      </TaskItemAnimated>
                    );
                  })}
                </TaskList>
              </div>
            )}

            {showResponse && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <SectionLabel $color={colors.brand.exxtraDark}>AI Response</SectionLabel>
                <AiBubble>{s.aiResponse}</AiBubble>
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>
      </AnimatedBody>
      <WipBanner>Work in Progress</WipBanner>
    </PanelContainer>
  );
};

// ============================================
// Product Slide — Main Component
// ============================================

export const WhyNowSlide: React.FC = () => {
  const [activeScenario, setActiveScenario] = useState(0);
  const [revealIndex, setRevealIndex] = useState(-1);

  const scenario = SCENARIOS[activeScenario];
  const totalSteps = scenario.aiTasks.length + 1; // tasks + final response

  const resetToScenario = useCallback((idx: number) => {
    setActiveScenario(idx);
    setRevealIndex(-1);
  }, []);

  // Auto-advance timer
  useEffect(() => {
    const timer = setInterval(() => {
      setRevealIndex((prev) => {
        if (prev >= totalSteps) {
          const next = (activeScenario + 1) % SCENARIOS.length;
          resetToScenario(next);
          return -1;
        }
        return prev + 1;
      });
    }, STEP_INTERVAL);
    return () => clearInterval(timer);
  }, [activeScenario, totalSteps, resetToScenario]);

  // Compute architecture highlights from current stage
  const { nodes: highlightedNodes, connections: highlightedConnections } = useMemo(
    () => getHighlightsForStage(activeScenario, revealIndex),
    [activeScenario, revealIndex],
  );

  const highlightedPills = useMemo(
    () => getPillHighlights(activeScenario, revealIndex),
    [activeScenario, revealIndex],
  );

  return (
    <SlideLayout>
      <Header>
        <SlideTitle>Moms need high context AI solutions for Their Household</SlideTitle>
        <SlideSubtitle>
        B2C built on data was too generic and narrow for Moms Managing Households <br />Why Now: Memory Will Replace Data in B2C
        </SlideSubtitle>
      </Header>

      <ContentArea>
        <DiagramPanel>
          <ArchDiagramSvg
            highlightedNodes={highlightedNodes}
            highlightedConnections={highlightedConnections}
            highlightedPills={highlightedPills}
          />
        </DiagramPanel>

        <DemoPanel>
          <DemoPanelAnimated
            activeScenario={activeScenario}
            revealIndex={revealIndex}
            onSelectScenario={resetToScenario}
          />
        </DemoPanel>
      </ContentArea>
    </SlideLayout>
  );
};
